import type { MouseEvent as ReactMouseEvent, SyntheticEvent } from 'react';
import type { ViewModel } from '@/screens/generated/view-model';
import type { Ctx } from './ctx';

/** The three shared overlays the package added: a pointer-following figure tooltip, a detail drawer, and the
 *  Trading-day panel — ported from the reference's `hoverPack()`, `openDrill()` and the calendar's day handler.
 *
 *  They live in one slice because they share one rule: **nothing here holds data of its own.** A tooltip is a
 *  projection of whatever spec the hovered figure handed it, and the drawer is a projection of the spec the clicked
 *  figure handed it. Keeping the specs at the call site is what stops these becoming a second place where a number is
 *  computed — and a figure that disagrees with its own tooltip is worse than no tooltip.
 *
 *  Every fallback below is the reference's own (`'var(--text-mid)'`, `'ph-list-dashes'`, `'Open journal'`…), carried
 *  rather than chosen, because they are visible defaults.
 */

export interface TipRow {
  readonly k: string;
  readonly v: string;
  readonly col?: string;
}

/** What a hovered figure hands the tooltip. */
export interface TipSpec {
  readonly head: string;
  readonly note?: string;
  readonly value: string;
  readonly valueCol?: string;
  readonly rows?: readonly TipRow[];
}

export interface DrillRow {
  readonly name?: string;
  readonly sub?: string;
  readonly value?: string;
  readonly meta?: string;
  readonly lead?: string;
  readonly col?: string;
}

export interface DrillStat {
  readonly label: string;
  readonly value: string;
  readonly col?: string;
}

export interface DrillTrendBar {
  readonly h: string;
  readonly col: string;
  readonly label?: string;
  /** Bar opacity — the figures fade with magnitude so a flat sub-period reads as flat rather than as missing. */
  readonly op?: string;
  /** Bars carry their own tooltip spec, so the drawer's chart explains itself the same way every other figure does. */
  readonly tip?: TipSpec;
}

export interface DrillNote {
  readonly text: string;
  readonly icon?: string;
  readonly col?: string;
}

/** What a clicked figure hands the drawer. */
export interface DrillSpec {
  readonly kicker: string;
  readonly title: string;
  readonly sub?: string;
  readonly body?: string;
  readonly value: string;
  readonly valueCol?: string;
  readonly delta?: string;
  readonly deltaCol?: string;
  readonly rows?: readonly DrillRow[];
  readonly rowsTitle?: string;
  readonly rowsIcon?: string;
  readonly rowsMeta?: string;
  readonly stats?: readonly DrillStat[];
  readonly trend?: readonly DrillTrendBar[];
  readonly trendTitle?: string;
  readonly notes?: readonly DrillNote[];
  readonly ctaLabel?: string;
  readonly ctaIcon?: string;
  readonly ctaHint?: string;
  /** Where the call-to-action goes. The drawer closes first either way. */
  readonly go?: () => void;
}

/** The hover handlers a figure spreads onto itself to get a tooltip.
 *
 *  `hMove` is separate from `hEnter` because the tooltip tracks the pointer across the figure rather than pinning
 *  where it entered — a 400px-wide bar with a tooltip stuck at its left edge points at the wrong part of itself. */
export interface HoverPack {
  hEnter(event?: SyntheticEvent): void;
  hMove(event?: SyntheticEvent): void;
  hLeave(event?: SyntheticEvent): void;
}

/** The pointer position out of a synthetic event, or nothing.
 *
 *  Narrowed rather than assumed: these handlers are spread onto elements whose other events are not mouse events,
 *  and reading `clientX` off a focus event would position the tooltip at 0,0. */
const pointerOf = (e?: SyntheticEvent): { clientX: number; clientY: number } | undefined =>
  e && 'clientX' in e
    ? { clientX: (e as ReactMouseEvent).clientX, clientY: (e as ReactMouseEvent).clientY }
    : undefined;

/** Positions the tooltip at the pointer, flipping before it leaves the viewport.
 *
 *  Written against the element rather than through React state on purpose: this runs on every pointer move, and a
 *  `setState` per frame would re-render the whole screen to move one box eight pixels. The reference does the same,
 *  and it is the reason `tipRef` exists at all. */
export function moveTip(
  ref: { current: HTMLElement | null },
  p: { clientX: number; clientY: number } | undefined,
): void {
  const el = ref.current;
  if (!el || !p) return;
  const w = el.offsetWidth || 212;
  const h = el.offsetHeight || 92;
  let x = p.clientX + 15;
  let y = p.clientY + 17;
  // Flip to the other side of the pointer rather than clamping: a tooltip pinned to the edge covers the figure it
  // is describing.
  if (x + w > window.innerWidth - 10) x = p.clientX - w - 15;
  if (y + h > window.innerHeight - 10) y = p.clientY - h - 15;
  el.style.left = Math.max(8, x) + 'px';
  el.style.top = Math.max(8, y) + 'px';
}

/** Builds the handler trio a figure spreads onto itself. `undefined` for a figure with nothing to explain. */
export function hoverPack(c: Ctx, spec: TipSpec | undefined): HoverPack {
  return {
    hEnter(event) {
      moveTip(c.tipRef, pointerOf(event));
      c.set({ tip: spec ?? null });
    },
    hMove(event) {
      moveTip(c.tipRef, pointerOf(event));
    },
    hLeave() {
      // Guarded, because `hLeave` fires on figures that never set a tooltip and an unconditional set would re-render
      // the screen on every pointer exit anywhere.
      if (c.S.tip) c.set({ tip: null });
    },
  };
}

/** Opens the drawer for a figure, closing the pointer tooltip on the way.
 *
 *  Both, always: the click that opens the drawer is the tail of a hover, so leaving the tooltip up would float a
 *  small summary of the figure over the panel that now explains it properly. */
export function openDrill(c: Ctx, spec: DrillSpec): void {
  c.set({ drill: spec, tip: null });
}

export function buildOverlays(c: Ctx): Partial<ViewModel> {
  const t = c.S.tip;
  const d = c.S.drill;

  return {
    // ── the figure tooltip ─────────────────────────────────────────────────────────────────────────────────────
    tipRef: c.tipRef,
    tipHead: t ? t.head : '',
    tipNote: t?.note ?? '',
    tipValue: t ? t.value : '',
    tipValueCol: t?.valueCol ?? 'var(--text)',
    tipRows: t?.rows ? t.rows.map((r) => ({ k: r.k, v: r.v, col: r.col ?? 'var(--text-mid)' })) : [],
    // Opacity rather than mount/unmount: the element stays in the DOM so it can be measured and moved before it is
    // shown, which is what stops the first hover flashing it in the wrong place.
    tipOp: t ? '1' : '0',

    // ── the detail drawer ──────────────────────────────────────────────────────────────────────────────────────
    drKicker: d ? d.kicker : '',
    drTitle: d ? d.title : '',
    drSub: d?.sub ?? '',
    drBody: d?.body ?? '',
    drValue: d ? d.value : '',
    drValueCol: d?.valueCol ?? 'var(--text)',
    drDelta: d?.delta ?? '',
    drDeltaCol: d?.deltaCol ?? 'var(--muted)',
    drRows: d?.rows ?? [],
    drHasRows: !!d?.rows?.length,
    drRowsTitle: d?.rowsTitle ?? '',
    drRowsIcon: d?.rowsIcon ?? 'ph-list-dashes',
    drRowsMeta: d?.rowsMeta ?? '',
    drStats: d?.stats
      ? d.stats.map((s) => ({ label: s.label, value: s.value, col: s.col ?? 'var(--text)' }))
      : [],
    drHasStats: !!d?.stats?.length,
    drTrend: d?.trend ? d.trend.map((b) => ({ op: '1', ...b, ...hoverPack(c, b.tip) })) : [],
    drHasTrend: !!d?.trend?.length,
    drTrendTitle: d?.trendTitle ?? 'Recent trend',
    drNotes: d?.notes
      ? d.notes.map((n) => ({
          icon: n.icon ?? 'ph-arrow-bend-down-right',
          col: n.col ?? 'var(--muted2)',
          text: n.text,
        }))
      : [],
    drHasNotes: !!d?.notes?.length,
    drCtaLabel: d?.ctaLabel ?? 'Open journal',
    drCtaIcon: d?.ctaIcon ?? 'ph-notebook',
    drCtaHint: d?.ctaHint ?? '',
    drOp: d ? '1' : '0',
    drPe: d ? 'auto' : 'none',
    drX: d ? '0px' : '26px',
    drClose: () => c.set({ drill: null, tip: null }),
    drCta: () => {
      // Closed before the navigation, not after: the drawer is describing the figure you are leaving, and leaving it
      // open over the destination is a panel about the wrong screen.
      const target = c.S.drill?.go;
      c.set({ drill: null, tip: null });
      target?.();
    },
  };
}
